/**
 * Si/No. Dos presentaciones para dos significados distintos:
 *
 * - `checkbox`: el usuario marca algo que forma parte de un conjunto ("Cliente",
 *   "Proveedor", "Empleado"). Se leen como una lista.
 * - `switch`: activa o desactiva un estado del registro ("Activo"). Tiene efecto
 *   inmediato en como se comporta el sistema, y por eso se ve como una llave.
 *
 * Los dos son elementos etiquetables, asi que la `<label>` del campo los nombra
 * sin necesidad de un `aria-label` duplicado.
 */

import clsx from 'clsx';

import type { FieldInputProps } from '@/components/fields/types';

export function BooleanField({
  id,
  meta,
  value,
  onChange,
  readonly,
  invalid,
  describedBy,
}: FieldInputProps) {
  const marcado = value === true;
  const esSwitch = meta.widget === 'switch';

  if (esSwitch) {
    return (
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={marcado}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        disabled={readonly}
        onClick={() => {
          onChange(!marcado);
        }}
        className={clsx(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors',
          'disabled:cursor-not-allowed disabled:opacity-60',
          marcado ? 'border-accent bg-accent' : 'border-line bg-surface-raised',
        )}
      >
        <span
          aria-hidden="true"
          className={clsx(
            'inline-block h-4 w-4 rounded-full bg-surface shadow-sm transition-transform',
            marcado ? 'translate-x-6' : 'translate-x-1',
            marcado && 'bg-accent-contrast',
          )}
        />
      </button>
    );
  }

  return (
    <input
      id={id}
      type="checkbox"
      checked={marcado}
      disabled={readonly}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      className={clsx(
        'h-5 w-5 shrink-0 cursor-pointer rounded border-line text-accent accent-[rgb(var(--accent))]',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
      onChange={(evento) => {
        onChange(evento.target.checked);
      }}
    />
  );
}
